import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTodos, type ListKey } from './useTodos'

/**
 * TO-DO Notebook — a private, per-agent paper-notebook to-do list, docked to the
 * right edge of every authenticated screen. Recreated from the design handoff
 * prototype at high fidelity. It deliberately does NOT use the dashboard design
 * tokens for its interior — the paper aesthetic (cream, ruled lines, red margin)
 * is intentional. Only the dock tab + accents use the brand blue.
 *
 * Standalone: it reads/writes its own localStorage keys and mounts once in
 * Layout. It does not touch any existing feature, route, socket, or store.
 */

const LABELS: Record<ListKey, string> = { today: 'Today', tomorrow: 'Tomorrow', week: 'This Week', pending: 'Pending' }
const SHORT: Record<ListKey, string> = { today: 'Today', tomorrow: 'Tmrw', week: 'Week', pending: 'Pending' }
const TAB_ORDER: ListKey[] = ['today', 'tomorrow', 'week', 'pending']
const MOVE_TARGETS: ListKey[] = ['today', 'tomorrow', 'week']

const FONT = "'Mulish', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

/* Hover / reveal rules that can't be expressed as inline styles. Injected once. */
const CSS = `
@keyframes todoSlideIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
@keyframes todoFadeIn{from{opacity:0}to{opacity:1}}
.wq-todo .wqr{opacity:0;transition:opacity .1s}
.wq-todo .wqh:hover .wqr{opacity:1}
.wq-todo .wq-iconbtn:hover{background:rgba(0,0,0,0.06);color:#57534e}
.wq-todo .wq-grip:hover{color:#8a8069}
.wq-todo .wq-move:hover{color:#2563eb;background:rgba(37,99,235,0.08)}
.wq-todo .wq-del:hover{color:#ef4444;background:rgba(239,68,68,0.08)}
.wq-todo .wq-moveopt:hover{background:#f3eee1}
.wq-todo .wq-subrow:hover{background:#faf7ee}
.wq-todo .wq-subdel:hover{color:#ef4444}
.wq-todo input::placeholder,.wq-todo textarea::placeholder{color:#b8ad98}
`

/* Inline SVG icons (Heroicons-style outline, stroke-width 2). */
const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg style={{ width: size, height: size, flex: 'none' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
)
const IconExpand = () => (
  <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></svg>
)
const IconCollapse = () => (
  <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" /></svg>
)
const IconX = ({ size = 14 }: { size?: number }) => (
  <svg style={{ width: size, height: size }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
)
const IconGrip = () => (
  <svg style={{ width: 12, height: 12 }} viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>
)
const IconArrow = () => (
  <svg style={{ width: 12, height: 12 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
)
const IconCheckMark = ({ size = 11 }: { size?: number }) => (
  <svg style={{ width: size, height: size, color: '#57534e' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
)
const IconPlus = () => (
  <svg style={{ width: 14, height: 14, color: '#a8a29e', flex: 'none' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
)

export function TodoNotebook() {
  const { currentAgent } = useAuth()
  const userId = currentAgent?.id ?? null
  const initial = (currentAgent?.name?.trim()?.[0] || 'A').toUpperCase()
  const t = useTodos(userId)

  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [tab, setTab] = useState<ListKey>('today')
  const [draft, setDraft] = useState('')
  const [subDraft, setSubDraft] = useState('')
  const [subFilter, setSubFilter] = useState<'incomplete' | 'completed'>('incomplete')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [moveOpenId, setMoveOpenId] = useState<string | null>(null)
  const [reorderingId, setReorderingId] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [panelDragging, setPanelDragging] = useState(false)
  const [, forceTick] = useState(0)

  const [tabY, setTabY] = useState<number>(() => {
    try { const v = parseInt(localStorage.getItem('wq_todo_tab_y') || '', 10); return isNaN(v) ? 300 : v } catch { return 300 }
  })
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(() => {
    try { return JSON.parse(localStorage.getItem('wq_todo_panel_pos') || 'null') } catch { return null }
  })

  const expandedRef = useRef(expanded); expandedRef.current = expanded

  // Recompute geometry on viewport resize.
  useEffect(() => {
    const onResize = () => forceTick((n) => n + 1)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Close the move popover on any outside click. Toggling it stops propagation,
  // so this only fires for genuine outside clicks.
  useEffect(() => {
    const onDocClick = () => setMoveOpenId(null)
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  /* ── Drag handlers (pointer events + window listeners) ─────────────────── */

  const tabDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    const startY = e.clientY
    const startTab = tabY
    let moved = false
    let lastY = startTab
    const onMove = (ev: PointerEvent) => {
      const dy = ev.clientY - startY
      if (Math.abs(dy) > 4) moved = true
      if (moved) {
        lastY = Math.min(Math.max(startTab + dy, 64), window.innerHeight - 140)
        setTabY(lastY)
        setDragging(true)
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (!moved) { setOpen((o) => !o); setDragging(false) }
      else { try { localStorage.setItem('wq_todo_tab_y', String(Math.round(lastY))) } catch { /* ignore */ } setDragging(false) }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [tabY])

  const gripDown = useCallback((tabKey: ListKey, id: string) => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation()
    const row = (e.currentTarget as HTMLElement).closest('.wqh') as HTMLElement | null
    const listEl = row?.parentElement
    if (!row || !listEl) return
    const rows = () => Array.from(listEl.querySelectorAll('.wqh')) as HTMLElement[]
    let curIdx = rows().indexOf(row)
    setReorderingId(id)
    const onMove = (ev: PointerEvent) => {
      const rs = rows()
      let target = curIdx
      rs.forEach((r, i) => {
        const rc = r.getBoundingClientRect()
        if (ev.clientY > rc.top && ev.clientY < rc.bottom) target = i
      })
      if (target !== curIdx) {
        t.reorder(tabKey, curIdx, target)
        curIdx = target
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      setReorderingId(null)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [t])

  const panelDragDown = useCallback((e: React.PointerEvent) => {
    if (expandedRef.current) return
    e.preventDefault()
    const panel = (e.currentTarget as HTMLElement).parentElement
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    const offX = e.clientX - rect.left
    const offY = e.clientY - rect.top
    let moved = false
    let last: { x: number; y: number } | null = null
    const onMove = (ev: PointerEvent) => {
      if (!moved && Math.abs(ev.clientX - (rect.left + offX)) < 4 && Math.abs(ev.clientY - (rect.top + offY)) < 4) return
      moved = true
      const x = Math.min(Math.max(ev.clientX - offX, 8), window.innerWidth - 100)
      const y = Math.min(Math.max(ev.clientY - offY, 8), window.innerHeight - 60)
      last = { x, y }
      setPanelPos(last)
      setPanelDragging(true)
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (moved && last) { try { localStorage.setItem('wq_todo_panel_pos', JSON.stringify(last)) } catch { /* ignore */ } }
      setPanelDragging(false)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  /* ── Actions ──────────────────────────────────────────────────────────── */

  const close = () => {
    try { localStorage.removeItem('wq_todo_panel_pos') } catch { /* ignore */ }
    setOpen(false); setExpanded(false); setPanelPos(null)
  }
  const addTaskNow = () => { if (draft.trim()) { t.addTask(tab, draft); setDraft('') } }
  const addSubNow = () => {
    if (subDraft.trim() && selectedId) { t.addSubtask(selectedId, subDraft); setSubDraft('') }
  }
  const removeTaskNow = (id: string) => { t.removeTask(id); if (selectedId === id) setSelectedId(null) }

  // Nothing to dock until we know who the agent is.
  if (!userId) return null

  /* ── Derived geometry (mirrors the prototype exactly) ─────────────────── */

  const list = t.todos[tab] || []
  const panelH = expanded ? Math.min(640, window.innerHeight - 64) : 520
  const top = expanded
    ? Math.max(32, (window.innerHeight - panelH) / 2)
    : Math.min(Math.max(tabY - 160, 66), Math.max(66, window.innerHeight - panelH - 16))
  const panelW = expanded ? Math.min(880, window.innerWidth - 96) : 360

  const sel = selectedId ? t.findTask(selectedId) : null
  const selTask = sel?.task ?? null
  const selKey = sel?.key ?? null

  const dateLabel = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })

  const tabStyle: CSSProperties = {
    position: 'fixed', right: 0, top: tabY, zIndex: 90, display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 6, padding: '10px 5px', width: 28, boxSizing: 'border-box',
    borderRadius: '10px 0 0 10px', background: open ? '#1d4ed8' : '#2563eb', color: '#fff',
    boxShadow: '-2px 2px 8px rgba(15,23,42,0.25)', cursor: dragging ? 'grabbing' : 'pointer',
    userSelect: 'none', touchAction: 'none',
  }

  const panelStyle: CSSProperties = expanded
    ? {
        position: 'fixed', left: '50%', transform: 'translateX(-50%)', top, zIndex: 89, width: panelW,
        height: panelH, display: 'flex', flexDirection: 'column', background: '#f3eee1', borderRadius: 12,
        border: '1px solid #e0d9c8', boxShadow: '0 24px 60px rgba(15,23,42,0.35)', padding: '10px 10px 12px 10px',
        boxSizing: 'border-box', animation: 'todoFadeIn .18s ease-out',
      }
    : {
        position: 'fixed', ...(panelPos ? { left: Math.round(panelPos.x), top: Math.round(panelPos.y) } : { right: 36, top }),
        zIndex: 89, width: 360, height: panelH, display: 'flex', flexDirection: 'column', background: '#f3eee1',
        borderRadius: 12, border: '1px solid #e0d9c8', boxShadow: '0 16px 40px rgba(15,23,42,0.22)',
        padding: '10px 10px 12px 10px', boxSizing: 'border-box',
        ...(panelDragging ? {} : { animation: 'todoSlideIn .18s ease-out' }),
      }

  return createPortal(
    <div className="wq-todo" style={{ fontFamily: FONT }}>
      <style>{CSS}</style>

      {/* ── Docked tab (always visible) ─────────────────────────────────── */}
      <div onPointerDown={tabDown} title="My To-Do list" style={tabStyle}>
        <IconCheck size={16} />
        <span style={{ writingMode: 'vertical-rl', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>TO-DO</span>
      </div>

      {open && (
        <>
          {expanded && (
            <div onClick={() => setExpanded(false)} style={{ position: 'fixed', inset: 0, zIndex: 88, background: 'rgba(15,23,42,0.45)', animation: 'todoFadeIn .15s ease-out' }} />
          )}

          <div style={panelStyle}>
            {/* Header strip (drag handle) */}
            <div onPointerDown={panelDragDown} title="Drag to move" style={{ display: 'flex', alignItems: 'flex-end', padding: '0 8px', gap: 2, cursor: expanded ? 'default' : panelDragging ? 'grabbing' : 'grab', touchAction: 'none' }}>
              <div style={{ alignSelf: 'center', display: 'flex', flexDirection: 'column', gap: 2, padding: '0 4px 2px 0', pointerEvents: 'none' }}>
                <span style={{ display: 'block', width: 14, height: 2, borderRadius: 2, background: '#d6cdb8' }} />
                <span style={{ display: 'block', width: 14, height: 2, borderRadius: 2, background: '#d6cdb8' }} />
              </div>
              <div onClick={() => setExpanded((v) => !v)} title={expanded ? 'Collapse' : 'Expand'} className="wq-iconbtn" style={{ marginBottom: 6, marginRight: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, color: '#9a8f7a', cursor: 'pointer' }}>
                {expanded ? <IconCollapse /> : <IconExpand />}
              </div>
              {TAB_ORDER.map((k) => {
                const active = tab === k
                const count = (t.todos[k] || []).filter((x) => !x.done).length
                const pendingHot = k === 'pending' && count > 0
                const showCount = expanded ? count : active ? 0 : count
                const tabBtnStyle: CSSProperties = {
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: expanded ? '7px 10px 9px 10px' : '6px 7px 8px 7px', marginBottom: -1,
                  borderRadius: '8px 8px 0 0', fontSize: expanded ? 12 : 11, fontWeight: 700, cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  border: active ? '1px solid #e0d9c8' : '1px solid transparent',
                  ...(active ? { borderBottomColor: '#fffdf6', background: '#fffdf6', color: '#44403c' } : { color: pendingHot ? '#b45309' : '#8a8069' }),
                }
                const pillStyle: CSSProperties = {
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 14, height: 14,
                  padding: '0 3px', borderRadius: 9999, fontSize: 9, fontWeight: 700,
                  ...(k === 'pending' ? { background: '#f59e0b', color: '#fff' } : active ? { background: '#2563eb', color: '#fff' } : { background: 'rgba(0,0,0,0.08)', color: '#6d6458' }),
                }
                return (
                  <div key={k} onClick={() => { setTab(k); setMoveOpenId(null) }} style={tabBtnStyle}>
                    {expanded ? LABELS[k] : SHORT[k]}
                    {showCount > 0 && <span style={pillStyle}>{showCount}</span>}
                  </div>
                )
              })}
              <div onClick={close} title="Close" className="wq-iconbtn" style={{ marginLeft: 'auto', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, color: '#9a8f7a', cursor: 'pointer' }}>
                <IconX />
              </div>
            </div>

            {/* Body: paper page (+ detail pane when expanded) */}
            <div style={{ display: 'flex', flex: 1, minHeight: 0, background: '#fffdf6', borderRadius: '2px 2px 10px 10px', boxShadow: '0 1px 0 rgba(0,0,0,0.06) inset', overflow: 'hidden' }}>
              {/* Paper page */}
              <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Red margin lines */}
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 34, width: 1, background: 'rgba(248,113,113,0.55)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 37, width: 1, background: 'rgba(248,113,113,0.35)', pointerEvents: 'none' }} />
                {/* Punched holes */}
                <div style={{ position: 'absolute', left: 10, top: 70, width: 9, height: 9, borderRadius: 9999, background: '#efe9dc', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.18)' }} />
                <div style={{ position: 'absolute', left: 10, top: '50%', width: 9, height: 9, borderRadius: 9999, background: '#efe9dc', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.18)' }} />
                <div style={{ position: 'absolute', left: 10, bottom: 70, width: 9, height: 9, borderRadius: 9999, background: '#efe9dc', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.18)' }} />

                {/* Page header */}
                <div style={{ padding: '12px 16px 6px 48px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#44403c' }}>{LABELS[tab]}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                    <button
                      onClick={() => window.location.assign('/crm/my-day?tab=todos')}
                      title="Open My Day on the To-dos tab"
                      style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                    >Go to My Day →</button>
                    <span style={{ fontSize: 10, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{dateLabel}</span>
                  </span>
                </div>

                {/* Add-task input (hidden on Pending) */}
                {tab !== 'pending' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 16px 0 48px', padding: '7px 10px', borderRadius: 6, border: '1px solid #e0d9c8', background: '#fff' }}>
                    <IconPlus />
                    <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addTaskNow() }} placeholder="Add a task..." style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 13, color: '#44403c', fontFamily: FONT }} />
                  </div>
                )}

                {/* Pending banner */}
                {tab === 'pending' && (
                  <div style={{ margin: '0 16px 0 48px', padding: '7px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 11, color: '#92400e' }}>Tasks from Today that weren't completed land here.</div>
                )}

                {/* Ruled task list */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 10, backgroundImage: 'repeating-linear-gradient(to bottom,transparent 0px,transparent 31px,#dccfb4 31px,#dccfb4 32px)', backgroundAttachment: 'local' }}>
                  {list.map((task) => {
                    const rowStyle: CSSProperties = {
                      display: 'flex', alignItems: 'center', gap: 8, height: 31, marginBottom: 1, boxSizing: 'border-box',
                      padding: '0 12px 0 48px', cursor: 'pointer',
                      ...(reorderingId === task.id
                        ? { background: '#e0e7ff', boxShadow: '0 2px 8px rgba(15,23,42,0.12)', position: 'relative', zIndex: 5 }
                        : selectedId === task.id && expanded
                        ? { background: '#dbeafe' }
                        : task.done
                        ? { background: '#fef9c3' }
                        : {}),
                    }
                    const boxStyle: CSSProperties = {
                      display: 'flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 3,
                      border: `1px solid ${task.done ? '#a8a29e' : '#c8c0ae'}`, background: '#fff', cursor: 'pointer', flex: 'none',
                    }
                    const textStyle: CSSProperties = {
                      flex: 1, minWidth: 0, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      color: task.done ? '#78716c' : '#44403c',
                    }
                    const meta = tab === 'pending' && task.from
                      ? 'from ' + new Date(task.from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                      : ''
                    return (
                      <div key={task.id} className="wqh" onClick={() => { if (expanded) setSelectedId(task.id) }} style={rowStyle}>
                        <div onPointerDown={gripDown(tab, task.id)} title="Drag to reorder" className="wqr wq-grip" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 20, marginLeft: -22, color: '#c8c0ae', cursor: 'grab', flex: 'none', touchAction: 'none' }}>
                          <IconGrip />
                        </div>
                        <div onClick={(e) => { e.stopPropagation(); t.toggleTask(task.id) }} style={boxStyle}>
                          {task.done && <IconCheckMark size={11} />}
                        </div>
                        <span style={textStyle}>{task.text}</span>
                        {meta && <span style={{ fontSize: 9, color: '#b8ad98', flex: 'none' }}>{meta}</span>}
                        <div style={{ position: 'relative', flex: 'none' }}>
                          <div onClick={(e) => { e.stopPropagation(); setMoveOpenId((cur) => (cur === task.id ? null : task.id)) }} className="wqr wq-move" title="Move to..." style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, color: '#a8a29e', cursor: 'pointer' }}>
                            <IconArrow />
                          </div>
                          {moveOpenId === task.id && (
                            <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20, width: 150, borderRadius: 8, background: '#fff', border: '1px solid #e0d9c8', boxShadow: '0 8px 20px rgba(0,0,0,0.15)', padding: 4, animation: 'todoFadeIn .12s ease-out' }}>
                              <p style={{ margin: 0, padding: '4px 8px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#a8a29e' }}>Move to</p>
                              {MOVE_TARGETS.filter((k) => k !== tab).map((k) => (
                                <div key={k} onClick={(e) => { e.stopPropagation(); t.moveTask(task.id, k); setMoveOpenId(null) }} className="wq-moveopt" style={{ padding: '6px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600, color: '#44403c', cursor: 'pointer' }}>{LABELS[k]}</div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div onClick={(e) => { e.stopPropagation(); removeTaskNow(task.id) }} className="wqr wq-del" title="Delete task" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, color: '#a8a29e', cursor: 'pointer', flex: 'none' }}>
                          <IconX size={12} />
                        </div>
                      </div>
                    )
                  })}
                  {list.length === 0 && (
                    <div style={{ padding: '0 16px 0 48px', height: 31, display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: '#b8ad98', fontStyle: 'italic' }}>{tab === 'pending' ? 'Nothing pending — nice work!' : 'Nothing here yet — add a task above'}</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{ flex: 'none', padding: '6px 16px 10px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: '#b8ad98' }}>{list.filter((x) => x.done).length} of {list.length} done</span>
                  <span style={{ fontSize: 10, color: '#b8ad98' }}>Only you can see this list</span>
                </div>
              </div>

              {/* Detail pane (expanded only) */}
              {expanded && (
                <div style={{ width: '46%', flex: 'none', borderLeft: '1px solid #e7e0d0', background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {selTask ? (
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
                        <div onClick={() => t.toggleTask(selTask.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, marginTop: 4, borderRadius: 4, border: '1px solid #c8c0ae', background: '#fff', cursor: 'pointer', flex: 'none' }}>
                          {selTask.done && <IconCheckMark size={12} />}
                        </div>
                        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, lineHeight: 1.3, flex: 1, color: selTask.done ? '#a8a29e' : '#292524', ...(selTask.done ? { textDecoration: 'line-through' } : {}) }}>{selTask.text}</h2>
                      </div>
                      <p style={{ margin: '0 0 20px 26px', fontSize: 11, color: '#a8a29e' }}>list&nbsp;<span style={{ color: '#2563eb', fontWeight: 600 }}>{selKey ? LABELS[selKey] : ''}</span></p>

                      {/* Subtasks */}
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderBottom: '1px solid #eee7d8', paddingBottom: 6, marginBottom: 10 }}>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#44403c' }}>Subtasks</h3>
                        <div style={{ display: 'flex', gap: 2 }}>
                          <div onClick={() => setSubFilter('incomplete')} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', ...(subFilter === 'incomplete' ? { background: '#44403c', color: '#fff' } : { color: '#a8a29e' }) }}>Incomplete</div>
                          <div onClick={() => setSubFilter('completed')} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', ...(subFilter === 'completed' ? { background: '#44403c', color: '#fff' } : { color: '#a8a29e' }) }}>Completed</div>
                        </div>
                      </div>
                      <input value={subDraft} onChange={(e) => setSubDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addSubNow() }} placeholder="Add a subtask..." style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #e0d9c8', background: '#fff', fontSize: 13, color: '#44403c', outline: 'none', fontFamily: FONT, marginBottom: 8 }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 24 }}>
                        {selTask.subtasks.filter((st) => (subFilter === 'completed' ? st.done : !st.done)).map((st) => (
                          <div key={st.id} className="wqh wq-subrow" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 5 }}>
                            <div onClick={() => t.toggleSubtask(selTask.id, st.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: 3, border: '1px solid #c8c0ae', background: '#fff', cursor: 'pointer', flex: 'none' }}>
                              {st.done && <IconCheckMark size={10} />}
                            </div>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: st.done ? '#a8a29e' : '#44403c', ...(st.done ? { textDecoration: 'line-through' } : {}) }}>{st.text}</span>
                            <div onClick={() => t.removeSubtask(selTask.id, st.id)} className="wqr wq-subdel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 4, color: '#a8a29e', cursor: 'pointer', flex: 'none' }}>
                              <IconX size={11} />
                            </div>
                          </div>
                        ))}
                        {selTask.subtasks.filter((st) => (subFilter === 'completed' ? st.done : !st.done)).length === 0 && (
                          <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#b8ad98', fontStyle: 'italic' }}>{subFilter === 'completed' ? 'No completed subtasks' : 'No subtasks yet'}</p>
                        )}
                      </div>

                      {/* Notes */}
                      <h3 style={{ margin: '0 0 10px 0', fontSize: 15, fontWeight: 700, color: '#44403c', borderBottom: '1px solid #eee7d8', paddingBottom: 6 }}>Notes</h3>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 6, background: '#dbeafe', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flex: 'none' }}>{initial}</div>
                        <textarea value={selTask.note || ''} onChange={(e) => t.setNote(selTask.id, e.target.value)} placeholder="Add a note..." rows={4} style={{ flex: 1, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #e0d9c8', background: '#fff', fontSize: 13, lineHeight: 1.5, color: '#44403c', outline: 'none', resize: 'vertical', fontFamily: FONT }} />
                      </div>
                    </div>
                  ) : (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
                      <svg style={{ width: 36, height: 36, color: '#d6cdb8', marginBottom: 10 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#8a8069' }}>Select a task</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: 11, color: '#b8ad98' }}>Click a task on the page to add subtasks and notes</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>,
    document.body
  )
}
